import type { DesignElementDepth, PlacedDesignElement } from '../api/tendApi';

/**
 * Depth, flip and opacity of a placed design element — the one reading of
 * the three optional placement fields, shared by the two editors and both
 * Remotion compositions. The image render mirrors it in Python
 * (overlay_renderer._build_design_elements_for_render + overlay.html).
 *
 * - depth: 'back' draws the element above the gradient and the border but
 *   under the set elements; anything else, including every placement made
 *   before the field existed, is 'front' — the top-most layer, today's look.
 * - flipX mirrors the artwork left↔right in its OWN frame (under the
 *   rotation), so a flipped arrow stays flipped as it turns.
 * - opacity is 0–100 in the config (what the slider shows), 0–1 in CSS.
 */
export const DESIGN_ELEMENT_DEFAULT_OPACITY = 100;

export function designElementDepth(p: Pick<PlacedDesignElement, 'depth'>): DesignElementDepth {
  return p.depth === 'back' ? 'back' : 'front';
}

/** 0–1 for CSS. Absent or non-numeric = solid; out of range clamps. */
export function designElementOpacity(p: Pick<PlacedDesignElement, 'opacity'>): number {
  const raw = p.opacity;
  if (typeof raw !== 'number' || !Number.isFinite(raw)) return 1;
  return Math.max(0, Math.min(100, raw)) / 100;
}

/** The mirror alone — the editor puts it on the <img>, under the wrapper's rotate. */
export function designElementFlipTransform(p: Pick<PlacedDesignElement, 'flipX'>): string | undefined {
  return p.flipX ? 'scaleX(-1)' : undefined;
}

/** The whole transform for a renderer that positions the <img> itself (Remotion). */
export function designElementRenderTransform(p: Pick<PlacedDesignElement, 'rotation' | 'flipX'>): string {
  return `translate(-50%, -50%) rotate(${p.rotation || 0}deg)${p.flipX ? ' scaleX(-1)' : ''}`;
}

/** The two layers, keeping list order inside each (the last placed is on top). */
export function splitDesignElementsByDepth<T extends Pick<PlacedDesignElement, 'depth'>>(
  list: readonly T[] | null | undefined,
): { back: T[]; front: T[] } {
  const back: T[] = [];
  const front: T[] = [];
  for (const p of list || []) (designElementDepth(p) === 'back' ? back : front).push(p);
  return { back, front };
}
